/**
 * employeeOperations — one reading of a Digital Employee's operation (FULL-0100 / PROD-OPSUX-014).
 *
 * MASTER_FLOW §15A asks the agent console to make the MONITOR/OPERATE mental model real: an
 * operator must be able to tell, for one named employee, what it is doing, what it is waiting on
 * and what failed — from one consistent interface, without reading logs or secrets. The product
 * already had that answer on the server and no way to read it in the product: every other operator
 * view of this question is fleet-wide (helpdesk home, workforce console, attention inbox, approval
 * center), and the one per-employee operations projection —
 *
 *     GET /api/agent-runs/workload-summary/{agent_id}      (DIGEMP-AGENT-WORKLOAD-DETAIL-BACKEND-006)
 *
 * — had zero consumers in `frontend/src`.
 *
 * This module is the reading half of that endpoint, and nothing else. It fetches nothing, decides
 * nothing, writes nothing and grants nothing: it maps the payload's five buckets onto the operator
 * questions and hands every row the shared §14C.1 outcome vocabulary, so a run cannot read as
 * "בעבודה" here and something else on the workforce console.
 *
 * THE LANES. The server partitions one employee's live runs into four buckets plus a terminal one
 * (`agent_runs._workload_detail_live_buckets` + the `failed_recently` clause). Each bucket is
 * outcome-UNIFORM — every status it admits maps to the same §14C.1 outcome — which is why a lane
 * can carry an outcome for its rows rather than each row being read independently. That uniformity
 * is not assumed: `employeeOperations.test.js` parses the status sets out of the router source and
 * asserts it, so a sixth bucket or a widened bucket reddens the suite instead of drawing rows under
 * a heading that no longer describes them.
 *
 * WHAT THE SERVER DELIBERATELY DOES NOT SEND, stated so this screen is not read as hiding it: the
 * bucket rows carry identifiers, the stored status and timestamps — never `analysis`, `context`,
 * summaries or prompt text (see `AgentWorkloadRunRef`). "What failed" is therefore *which* run
 * failed and *when*; the reason lives on the run page this module links to, under the run's own
 * permission and redaction rules. This screen never becomes a second, weaker way to read a payload.
 *
 * TRUTHFULNESS RULES (pinned by tests):
 *   - a bucket key the payload does not carry is reported as NOT REPORTED, never as an empty lane:
 *     "0 items" is a claim the source did not make, and a build behind the producer must not draw a
 *     clean bill of health out of a missing field;
 *   - a run status outside the producer's scale draws the shared explicit `unknown` chip and the
 *     named "מצב לא מזוהה" word — never the raw identifier, never a guessed outcome;
 *   - "needs approval" is not the same set as "waiting for a human": the review bucket also holds a
 *     resumable PAUSED run, which the approval routes refuse (`_APPROVABLE_STATES`). The lane
 *     counts both and marks which is which, so the operator is never sent to an approve button that
 *     will 409;
 *   - the failure lane discloses the server's own window (`recent_failure_window_hours`), and says
 *     so instead of implying it when the field is absent.
 */
import { runStatusLabel } from "./runStatusVocabulary";
import { workOutcomeForRunStatus } from "./workOutcomeVocabulary";

/**
 * The statuses the approve/reject routes accept — pinned by test against `_APPROVABLE_STATES` in
 * `backend/app/routers/agent_runs.py`, because "this run needs your approval" is a claim about
 * those routes and not about this file.
 */
export const HUMAN_DECISION_STATUSES = ["awaiting_technician_review", "awaiting_approval"];

/** True when the approval decision routes can act on a run in this status. */
export function isHumanDecisionStatus(status) {
  return HUMAN_DECISION_STATUSES.includes(status);
}

/**
 * The lanes, in the order an operator reads them: what it is doing, what is waiting on a person,
 * what is waiting on the customer, what failed. `buckets` are the server's own key names — the
 * union across all lanes must equal the endpoint's bucket set exactly, which the test asserts.
 */
export const OPERATION_LANES = [
  {
    key: "doing",
    question: "what-is-it-doing",
    buckets: ["actively_handling"],
    outcome: "active",
    title: "מה העובד עושה כעת",
    description: "ריצות שהעובד מחזיק ברגע זה — עבודה פעילה או חיבור חי. אין כאן החלטה שממתינה לאדם.",
    emptyText: "אין ריצה פעילה לעובד זה כרגע",
  },
  {
    key: "human",
    question: "what-needs-a-human",
    buckets: ["waiting_for_technician_review", "waiting_for_approval"],
    outcome: "waiting",
    title: "מה ממתין לאדם",
    description:
      "ריצות שמושהות עד להחלטה אנושית. ההחלטה עצמה מתבצעת בעמוד הריצה — המסך הזה מציג בלבד ואינו מאשר דבר.",
    emptyText: "אין ריצה שממתינה להחלטה אנושית",
  },
  {
    key: "customer",
    question: "what-waits-on-the-customer",
    buckets: ["waiting_for_customer"],
    outcome: "waiting",
    title: "מה ממתין ללקוח",
    description: "ריצות שממתינות לחיבור או לתשובה מהלקוח — לא לפעולה של הצוות.",
    emptyText: "אין ריצה שממתינה ללקוח",
  },
  {
    key: "failed",
    question: "what-failed",
    buckets: ["failed_recently"],
    outcome: "blocked",
    title: "מה נכשל",
    description: "ריצות שהסתיימו בכשל בחלון שהשרת מצהיר עליו. סיבת הכשל נמצאת בעמוד הריצה.",
    emptyText: "לא נכשלו ריצות בחלון הזה",
  },
];

export const OPERATION_LANE_KEYS = OPERATION_LANES.map((lane) => lane.key);

/** Every server bucket any lane reads — the contract this module is total over. */
export const OPERATION_BUCKETS = OPERATION_LANES.flatMap((lane) => lane.buckets);

function isIsoLike(value) {
  return typeof value === "string" && value.trim() !== "";
}

/**
 * One bucket row from the server (`AgentWorkloadRunRef`), bounded and normalised. A row without a
 * run id is not a row: it is dropped rather than rendered as an unopenable item, because every
 * action this screen offers is a link to that run.
 *
 * `at` prefers `finished_at` and falls back to `created_at`: the server stamps `finished_at` for
 * every FAILED run (`manager_agent._mark_terminal`) and leaves it null while a run is live, so one
 * rule is correct for both a wait ("since when") and a failure ("when it failed").
 */
export function normalizeOperationRef(raw) {
  if (!raw || typeof raw !== "object") return null;
  const runId = typeof raw.run_id === "string" && raw.run_id.trim() !== "" ? raw.run_id : null;
  if (!runId) return null;
  const status = typeof raw.status === "string" && raw.status.trim() !== "" ? raw.status : null;
  const ticketId = typeof raw.ticket_id === "string" && raw.ticket_id.trim() !== "" ? raw.ticket_id : null;
  const ticketNumber = Number.isInteger(raw.ticket_number) ? raw.ticket_number : null;
  const at = isIsoLike(raw.finished_at) ? raw.finished_at : isIsoLike(raw.created_at) ? raw.created_at : null;
  return {
    runId,
    status,
    // null when the producer's scale does not hold this status; the chip renders it as `unknown`.
    outcome: workOutcomeForRunStatus(status),
    statusLabel: runStatusLabel(status),
    ticketId,
    ticketNumber,
    at,
    // A run the approve/reject routes can act on. False for the resumable PAUSED run the review
    // bucket also holds — which is a different decision (resume) on a different control.
    approvable: isHumanDecisionStatus(status),
  };
}

function ageMsOf(at, now) {
  if (!at) return null;
  const parsed = Date.parse(at);
  return Number.isNaN(parsed) ? null : Math.max(0, now - parsed);
}

/**
 * The four lanes over one payload, with each lane's membership resolved.
 *
 * `present` is false when ANY of the lane's buckets is absent from the payload — the lane then
 * renders as not-reported rather than as empty. `missingBuckets` names which, so the screen can say
 * which part of the source is behind instead of a vague "unknown".
 */
export function readOperationLanes(payload, now = Date.now()) {
  const source = payload && typeof payload === "object" ? payload : null;
  return OPERATION_LANES.map((lane) => {
    const missingBuckets = [];
    const items = [];
    for (const bucket of lane.buckets) {
      const raw = source ? source[bucket] : undefined;
      if (!Array.isArray(raw)) {
        missingBuckets.push(bucket);
        continue;
      }
      for (const entry of raw) {
        const item = normalizeOperationRef(entry);
        if (item) items.push(item);
      }
    }
    items.sort((a, b) => {
      const left = ageMsOf(a.at, now);
      const right = ageMsOf(b.at, now);
      // Oldest first — the operator's queue is a queue. A row with no readable timestamp sorts
      // last rather than first: an unknown age is not an old age.
      if (left == null && right == null) return 0;
      if (left == null) return 1;
      if (right == null) return -1;
      return right - left;
    });
    return {
      key: lane.key,
      question: lane.question,
      outcome: lane.outcome,
      title: lane.title,
      description: lane.description,
      emptyText: lane.emptyText,
      buckets: lane.buckets,
      present: missingBuckets.length === 0,
      missingBuckets,
      items,
      count: items.length,
      // Of the human lane's rows, the ones the approval routes will actually accept.
      approvableCount: items.filter((item) => item.approvable).length,
    };
  });
}

/** The window the server applied to `failed_recently`, or null when it did not report one. */
export function failureWindowHours(payload) {
  const value = payload && typeof payload === "object" ? payload.recent_failure_window_hours : null;
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? value : null;
}

/**
 * Counts for the screen's header. `reported` is false when the payload carries no lane at all, so
 * the tiles can render "—" instead of a zero the server never sent. `approvable` is the subset of
 * `human` the approval routes accept — kept beside it rather than replacing it, because the
 * difference (a resumable paused run) is exactly what the operator needs told apart.
 *
 * `openTotal` and `total` differ by the failure lane, and both are reported rather than one being
 * left implicit: "open" means work still in flight, and a failed run is not in flight. A screen
 * that summed every lane under an "open" label would overstate the queue by exactly the failures,
 * which is the number an operator is most likely to be looking at.
 */
export function operationTotals(lanes) {
  const byKey = new Map((lanes || []).map((lane) => [lane.key, lane]));
  // A lane whose bucket the payload did not carry has no count at all. Reading its item list would
  // answer 0, and a 0 in a totals tile is the same false claim as a 0 in the lane: "nothing here"
  // when the truth is "this was never reported". The tile renders the difference as "—".
  const countOf = (key) => {
    const lane = byKey.get(key);
    return lane && lane.present ? lane.count : null;
  };
  const reported = (lanes || []).some((lane) => lane.present);
  const total = (lanes || []).reduce((sum, lane) => sum + lane.count, 0);
  const failed = countOf("failed") ?? 0;
  return {
    reported,
    doing: countOf("doing"),
    human: countOf("human"),
    approvable: countOf("human") == null ? null : byKey.get("human").approvableCount,
    customer: countOf("customer"),
    failed: countOf("failed"),
    openTotal: total - failed,
    total,
  };
}
